import { IconButton, Stack, Tooltip } from "@mui/material";
import { observer } from "mobx-react";
import { CombinatorRuleType } from "models/RuleCombinator";
import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import AcxButton from "components/UI/AcxButton";
import WordsPhraseStore from "./WordsPhrasesStore";
import WordsPhraseItem from "./WordsPhrasesItem";
import { blueButtonWhiteBackgroundStyle } from "utils/Style";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxWideTooltip from "components/UI/AcxWideTooltip";
import theme from "Theme/AppTheme";
import { FilterLabel } from "../../shared";

interface Props {
    store: WordsPhraseStore;
}

const WordsPhraseInput: React.FunctionComponent<Props> = observer(
    ({ store }) => {
        const [isAddingItem, setIsAddingItem] = useState(false);

        const handleAdd = (combinator: CombinatorRuleType) => {
            store.addWordsPhrasesItem(combinator);
            setIsAddingItem(false);
        };

        const wordsPhrasesTooltipHTML = (
            <>
                <p>Select a condition and type a word or phrase.</p>
                <ul>
                    <li>
                        To search for a phrase, type it into the box: i.e. have
                        a nice day
                    </li>
                    <li>
                        If you'd like to search for multiple words separately,
                        add another condition and select AND or OR (i.e. have
                        AND nice AND day)
                    </li>
                    <li>
                        When using Starts With, you may only use one word. i.e.
                        insur would find results including insurance, insure, or
                        insures
                    </li>
                </ul>
            </>
        );

        const renderLabel = (
            <Stack direction="row" alignItems="center" flex={1}>
                <FilterLabel sx={{ mb: 0 }}>Words Or Phrases</FilterLabel>
                {store.isInputStandard ? (
                    <AcxWideTooltip title={wordsPhrasesTooltipHTML}>
                        <InfoIcon
                            sx={{
                                height: "16px",
                                width: "16px",
                                marginLeft: "4px",
                            }}
                            color="primary"
                        />
                    </AcxWideTooltip>
                ) : (
                    <Tooltip title="The advanced editor allows you to write your own search parameters from scratch using Full Text Search syntax, rather than using the conditions and search boxes in the standard editor.">
                        <InfoIcon
                            sx={{
                                height: "16px",
                                width: "16px",
                                marginLeft: "4px",
                            }}
                            color="primary"
                        />
                    </Tooltip>
                )}
            </Stack>
        );

        const renderAddBlockButton = (
            <IconButton
                size="small"
                onClick={() => {
                    if (store.wordsPhrasesItems.length === 0) {
                        store.addWordsPhrasesItem(0);
                    } else {
                        setIsAddingItem(true);
                    }
                }}
                sx={{ padding: 0 }}
            >
                <AddIcon
                    sx={{
                        border: "1px solid #E4E4E7",
                        borderRadius: "4px",
                    }}
                />
            </IconButton>
        );

        const renderAddAndOrButtons = (
            <Stack direction="row" gap={1} mt={1}>
                <AcxButton
                    id="words-phrases-filter-add-and-button"
                    onClick={() => handleAdd(CombinatorRuleType.And)}
                    style={blueButtonWhiteBackgroundStyle}
                >
                    + AND
                </AcxButton>
                <AcxButton
                    id="words-phrases-filter-add-or-button"
                    onClick={() => handleAdd(CombinatorRuleType.Or)}
                    style={blueButtonWhiteBackgroundStyle}
                >
                    + OR
                </AcxButton>
            </Stack>
        );

        return (
            <>
                <Stack
                    direction="row"
                    justifyContent={
                        store.isInputStandard ? "space-between" : "flex-start"
                    }
                    alignItems="center"
                    sx={{ paddingBottom: 1, flexBasis: "fit-content" }}
                >
                    {renderLabel}
                    {store.isInputStandard && renderAddBlockButton}
                </Stack>
                {store.isInputStandard ? (
                    <>
                        {store.wordsPhrasesItems.map((item, index) => (
                            <WordsPhraseItem
                                item={item}
                                index={index}
                                key={index}
                                store={store}
                            />
                        ))}
                        {isAddingItem && renderAddAndOrButtons}
                    </>
                ) : (
                    <AcxMainTextField
                        id="advanced-words-phrases-input"
                        value={store.wordsPhrasesString}
                        onChange={(e) => {
                            store.setWordsPhrasesString(e.target.value);
                        }}
                        multiline
                        placeholderText="Type to add..."
                        rowsMin={1}
                        rowsMax={3}
                        containerStyle={{ width: "100%" }}
                    />
                )}
                <AcxButton
                    id="application-filters-words-phrases-advanced-toggle"
                    onClick={() => {
                        store.wordsPhrasesString
                            ? store.toggleConfirmationModal()
                            : store.toggleStandardInput();
                    }}
                    color="white"
                    sx={{
                        color: theme.palette.blue.main,
                        backgroundColor: "transparent",
                        border: "none",
                        px: 0.5,
                        mt: 0.25,
                    }}
                >
                    {store.isInputStandard ? "Advanced" : "Standard"}
                </AcxButton>
            </>
        );
    },
);

export default WordsPhraseInput;
